import { roleLabels, type Role } from './enums';
import { ROLE_GRANTS, type RoleGrantCode } from './grants';
import type { Permission } from './permissions';

/**
 * Перевод упраздняемых ролей — таблица, по которой идут этапы 8 и 9 реформы доступа
 * (`docs/permissions-restructure-plan.md` §9 «было → стало», §13.2 «перевод ролей — тоже три
 * релиза»; ADR 0113).
 *
 * **Здесь только объявление, а не действие.** Файл отвечает на «кого куда переводят и что выдают
 * взамен»; переводит учётки миграция этапа migrate, выдаёт наборы заранее миграция prepare, а
 * сверяет обе стороны на живых данных скрипт `check:role-migration`. Один ответ на четверых:
 * разъехавшись, они выдали бы не тот набор не тем людям — и заметить это было бы нечем, потому что
 * каждая копия проверяла бы сама себя.
 *
 * **Почему таблица, а не разбор по месту.** Три площадочные роли переводятся в одну, и различие
 * между ними целиком переезжает в наборы: `shtab` — «Заказ техники», `rukstroy` — он же плюс «Виза
 * объекта», `commandant` — ничего. Записанное `if`'ами в миграции, в скрипте и в форме, это
 * различие живёт в трёх местах и в первом же исправлении расходится.
 *
 * Порядок строк — порядок этапов: сначала площадка (этап 8), потом отдел (этап 9). На поведение он
 * не влияет, а читается таблица сверху вниз.
 */
export interface RoleMigration {
  /** Этап плана: 8 — три роли площадки, 9 — руководитель отдела. */
  readonly stage: 8 | 9;
  /** Упраздняемая роль. Значение enum остаётся в базе мёртвым — удалять его план не берётся. */
  readonly from: Role;
  /** Роль после перевода. */
  readonly to: Role;
  /**
   * Наборы, которые перевод выдаёт держателю старой роли, — ровно то, чем она отличалась от
   * целевой. Пустой список у коменданта не пропуск: он и есть `site` без полномочий.
   */
  readonly grants: readonly RoleGrantCode[];
  /**
   * Права, которых у старой роли не было и которые перевод **добавляет**, — объявленное расширение
   * доступа, а не побочный эффект.
   *
   * Пустой список у трёх строк из четырёх означает тождество: пара «роль + наборы» равна старой
   * роли поимённо (§9, тест эквивалентности в `grants-contracts.test.ts`). Непустой — только у
   * коменданта, и это единственное расширение всей реформы, принятое решением №1 заказчика от
   * 17.08.2026: заявку на ремонт принтера в бытовке подаёт тот, кто на площадке.
   *
   * Список выписан поимённо, а не вычисляется на месте, потому что его читает сверка на живых
   * данных: расхождение, совпавшее с объявленным, — ожидаемый результат перевода, любое другое —
   * отказ. Вычисляемая разница ответила бы «расширение» на любое расхождение, включая ошибку.
   */
  readonly gains: readonly Permission[];
}

export const ROLE_MIGRATIONS: readonly RoleMigration[] = [
  // Штаб: разница с площадкой — заказ техники с недельным пакетом и аренда механизации, то есть
  // ровно составы двух наборов. Механизация попала сюда планом визы (Р8): модуль ушёл из состава
  // роли `site`, и без взведённого набора перевод отобрал бы его у каждого штаба молча.
  { stage: 8, from: 'shtab', to: 'site', grants: ['vehicle_ordering', 'mech_ordering'], gains: [] },
  // Руководитель строительства: то же плюс подпись — своим набором, потому что подписывает не
  // всякий, кто заказывает. Подпись у него одна на портал: «Виза объекта» несёт и заказ техники, и
  // неделю, и аренду механизации (план визы, Р2).
  {
    stage: 8,
    from: 'rukstroy',
    to: 'site',
    grants: ['vehicle_ordering', 'mech_ordering', 'site_approval'],
    gains: [],
  },
  /*
   * Комендант: `site` без единого набора. Единственная строка реформы, где прав становится больше,
   * — оргтехника, которой у коменданта нет, а у площадки есть: справочник на чтение и шесть прав
   * заказчика заявок на обслуживание (`SERVICE_REQUEST_CUSTOMER_PERMISSIONS`). Шестое полномочие
   * «Заявки на обслуживание», которое сняло бы расширение, отвергнуто решением №1: набор пришлось
   * бы выдавать всем комендантам поголовно, а сущность, существующая ради того, чтобы её всегда
   * выдавать, — это строка матрицы, записанная дважды.
   */
  {
    stage: 8,
    from: 'commandant',
    to: 'site',
    // Пустым список был до плана визы механизации (Р8): аренду комендант ведёт наравне с прочей
    // площадкой (ADR 0152, Р9), а у роли `site` модуля больше нет — набор возвращает ему ровно то,
    // что он имел. Утверждение «`site` без полномочий» этим не отменяется: оно было про РАЗНИЦУ с
    // остальными двумя ролями, и разница по-прежнему нулевая — заказа техники у него нет.
    grants: ['mech_ordering'],
    gains: [
      'officeEquipment.read',
      /*
       * Шестое право приехало сюда выпуском B волны кандидатов (ADR 0165, этап Э6 плана предмета
       * заявки): `officeEquipment.propose` вошло в `SERVICE_REQUEST_CUSTOMER_PERMISSIONS`, то есть
       * в состав роли `site`, — значит и разница перевода выросла ровно на него. Список выписан
       * поимённо и сверяется на равенство, поэтому строка здесь обязательна: без неё перевод
       * коменданта отвечал бы «приобретено сверх объявленного» и остановился бы, а с молчаливым
       * «не меньше» расширение доступа прошло бы незамеченным — ровно то, ради чего список и
       * заведён.
       *
       * Расширением сверх уже принятого решения №1 это не является: заказчик открыл коменданту
       * оргтехнику целиком в том объёме, в каком она есть у площадки, а сообщение о ненайденном
       * аппарате — часть заведения заявки, а не отдельная работа.
       */
      'officeEquipment.propose',
      'serviceRequests.read',
      'serviceRequests.create',
      'serviceRequests.update',
      'serviceRequests.delete',
      'serviceRequests.files',
    ],
  },
  // Руководитель отдела: вся разница с отделом — виза под заказом техники. Недели здесь нет и не
  // будет: неделю отдел читает, но не ведёт (ADR 0062).
  {
    stage: 9,
    from: 'department_head',
    to: 'department',
    grants: ['department_approval'],
    gains: [],
  },
];

/** Быстрый поиск по упраздняемой роли: таблица короткая, но спрашивают её на каждом сохранении. */
const BY_ROLE: ReadonlyMap<Role, RoleMigration> = new Map(ROLE_MIGRATIONS.map((m) => [m.from, m]));

/** Упраздняемые роли — те, что перечислены в таблице слева. Второго списка рядом не заводится. */
export const RETIRING_ROLES: readonly Role[] = ROLE_MIGRATIONS.map((m) => m.from);

/** Перевод, объявленный для этой роли; `null` — роль остаётся. */
export function roleMigrationOf(role: Role | null | undefined): RoleMigration | null {
  return role ? (BY_ROLE.get(role) ?? null) : null;
}

/** Упраздняется ли роль. Предикат, а не `includes` по месту: спрашивают форма, API и сверка. */
export function isRetiringRole(role: Role | null | undefined): boolean {
  return roleMigrationOf(role) !== null;
}

/**
 * Почему эту роль больше нельзя назначить — текст отказа или `null`, если назначение допустимо.
 *
 * **Закрытый вход — обязательная часть шага prepare, а не предосторожность** (план §13.2). Между
 * выдачей наборов держателям старой роли и переводом учёток проходит целый релиз, и всё это время
 * работающая версия старую роль знает: заведи она нового «штаба» — и перевод, который выдаёт наборы
 * по снимку, о нём не узнает, а `UPDATE` молча отберёт у человека заказ техники. Закрыть вход можно
 * только там, где учётки заводят, и только заранее.
 *
 * **Прежняя роль при этом сохраняется.** Проверяется не «какая роль в теле запроса», а «меняется ли
 * роль на упраздняемую»: правка телефона у действующего штаба присылает его роль обратно, и отказ
 * на неё означал бы, что до перевода такие учётки вообще нельзя редактировать. Отсюда два
 * аргумента, а не один.
 *
 * Текст называет замену поимённо — роль и наборы, — потому что администратору в этот момент нужно
 * не объяснение реформы, а следующее действие.
 */
export function retiringRoleIssue(
  next: Role | null | undefined,
  current: Role | null | undefined,
): string | null {
  if (!next || next === current) return null;
  const migration = roleMigrationOf(next);
  if (!migration) return null;
  const grants = migration.grants.map((code) => `«${ROLE_GRANTS[code].name}»`).join(' и ');
  const replacement = grants
    ? `выберите «${roleLabels[migration.to]}» и выдайте ${grants}`
    : `выберите «${roleLabels[migration.to]}»`;
  return `Роль «${roleLabels[next]}» упраздняется реформой доступа и больше не назначается: ${replacement}.`;
}
